import { Anchor, Badge, Box, Card, Group, Stack, Text } from 'components'
import type { FC } from 'react'
import { memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useArcTranslation } from 'hooks'
import type { ArcProject } from '../../types'
import { STATUS_DISPLAY_MAP } from '../../types'
import { getPlaceholderImage, getCategoryTextColor, getCategoryBackgroundColor } from '../../utils'
import { ArcProjectCardActions } from './ArcProjectCardActions'

interface ArcProjectCardProps {
  project: ArcProject
  canEdit: boolean
  isLibrary?: boolean
  backlink?: string
}

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

const DEFAULT_STATUS_CONFIG = { color: 'gray', labelKey: 'arc_projects.status_draft' }

/** Fixed card height for consistent grid layout */
const CARD_MIN_HEIGHT = 400

const cardStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: CARD_MIN_HEIGHT,
    height: '100%',
  },
  name: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: 48, // ~2 lines at 16px font + line-height
    lineHeight: 1.4,
  },
  synopsis: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: 60, // ~3 lines
    lineHeight: 1.4,
  },
}

export const ArcProjectCard: FC<ArcProjectCardProps> = memo(({
  project,
  canEdit,
  isLibrary = false,
  backlink,
}) => {
  const { t } = useArcTranslation()

  const imageUrl = getPlaceholderImage(project.id)
  const statusConfig = STATUS_DISPLAY_MAP[project.status] || DEFAULT_STATUS_CONFIG
  const projectUrl = backlink
    ? `/projects/${project.id}?back=${encodeURIComponent(backlink)}`
    : `/projects/${project.id}`

  const cleanSynopsis = useMemo(() => {
    if (!project.synopsis) return ''
    return stripHtml(project.synopsis).trim()
  }, [project.synopsis])

  const hasSynopsis = cleanSynopsis.length > 0

  const gradesText = useMemo(() => {
    const names = project.projectGrades
      .filter(pg => pg?.grade?.name)
      .map(pg => pg.grade.name)
    return names.join(', ')
  }, [project.projectGrades])

  const hasGrade = gradesText.length > 0
  const hasClassSection = !!project.groupName
  const hasCategories = project.categories.length > 0
  const gradeLabelKey = isLibrary
    ? 'arc_projects.suggested_grade_label'
    : 'arc_projects.grade_label'

  return (
    <Card shadow="sm" p={0} radius="md" style={cardStyles.root}>
      <Box
        sx={{
          position: 'relative',
          height: 130,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderTopLeftRadius: 'var(--mantine-radius-md)',
          borderTopRightRadius: 'var(--mantine-radius-md)',
          flexShrink: 0,
        }}
      >
        {!isLibrary && (
          <Badge
            color={statusConfig.color}
            variant="light"
            size="lg"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              fontSize: 13,
              fontWeight: 600,
              padding: '6px 12px',
            }}
          >
            {t(statusConfig.labelKey)}
          </Badge>
        )}
      </Box>

      <Stack spacing={8} p="md" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Router Link keeps the persistent Arc shell mounted (plain href
            reloads the document); cmd/ctrl/middle-click still open a new tab */}
        <Anchor component={Link} to={projectUrl} underline={false} sx={{ color: 'var(--mantine-color-violet-6)' }}>
          <Text size="md" weight={500} style={cardStyles.name}>
            {project.name}
          </Text>
        </Anchor>

        <Box style={{ flex: 1 }}>
          <Text size="sm" color="dimmed" style={cardStyles.synopsis}>
            {hasSynopsis ? cleanSynopsis : '-'}
          </Text>
        </Box>

        {hasCategories && (
          <Group spacing={6} my="sm">
            {project.categories.map(category => (
              <Badge
                key={category.id}
                variant="filled"
                radius="sm"
                sx={{
                  backgroundColor: getCategoryBackgroundColor(category.color),
                  color: getCategoryTextColor(category.color),
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                {category.name}
              </Badge>
            ))}
          </Group>
        )}

        {(hasGrade || hasClassSection) && (
          <Group grow noWrap align="flex-start" spacing="md">
            {hasGrade && (
              <Box>
                <Text size="xs" color="dimmed" transform="uppercase" mb={2}>
                  {t(gradeLabelKey)}
                </Text>
                <Text size="sm" weight={500}>
                  {gradesText}
                </Text>
              </Box>
            )}
            {hasClassSection && (
              <Box>
                <Text size="xs" color="dimmed" transform="uppercase" mb={2}>
                  {t('arc_projects.group_label')}
                </Text>
                <Text size="sm" weight={500}>
                  {project.groupName}
                </Text>
              </Box>
            )}
          </Group>
        )}

        <Box style={{ marginTop: 'auto' }}>
          <ArcProjectCardActions
            project={project}
            canEdit={canEdit}
            isLibrary={isLibrary}
            backlink={backlink}
          />
        </Box>
      </Stack>
    </Card>
  )
})

ArcProjectCard.displayName = 'ArcProjectCard'
